import "./ExpensesPage.css";
import "../features/settings/settings-ui.css";
import { useCallback, useEffect, useMemo, useRef, useState } from "preact/hooks";
import { AltHintTarget } from "../components/AltHintTarget/AltHintTarget.jsx";
import { ControlBar } from "../components/ControlBar/ControlBar.jsx";
import { DashboardPage, createDashboardLayout } from "../components/DashboardPage/DashboardPage.jsx";
import { DataGrid } from "../components/DataGrid/DataGrid.jsx";
import { FloatingActionToolbar } from "../components/FloatingActionToolbar/FloatingActionToolbar.jsx";
import { StatusMessage } from "../components/StatusMessage/StatusMessage.jsx";
import { useStableSelectionState } from "../components/FloatingActionToolbar/useSlashActionToolbar.js";
import { Panel } from "../components/Panel/Panel.jsx";
import { SearchField } from "../components/SearchField/SearchField.jsx";
import { createExpenseGridColumns } from "../features/expenses/expenseGridColumns.js";
import {
  listExpenseMethodOptions,
  listExpenseServiceOptions,
  listExpenseSupplierOptions
} from "../features/expenses/expenseValueOptions.js";
import { formatCurrency } from "../lib/formatters.js";
import {
  listBudgetCategoryOptions,
  listExpenseRows,
  updateExpenseRows
} from "../lib/storage/walletWatchDb.js";
import { useShortcutBindings } from "../lib/shortcuts/useShortcutBindings.js";
import { onKey } from "../lib/shortcuts/onKey.js";
import { useRememberedSortState } from "../lib/useRememberedSortState.js";
import { useSessionStorageState } from "../lib/useSessionStorageState.js";
import {
  EXPENSES_SEARCH_SESSION_KEY,
} from "../lib/pageSearchState.js";
import { usePageStateMemory } from "../lib/usePageStateMemory.js";
import {
  EXPENSE_VISIBILITY_ALL,
  EXPENSE_VISIBILITY_LAST_10,
  EXPENSE_VISIBILITY_LAST_10_DAYS,
  EXPENSE_VISIBILITY_LAST_30,
  EXPENSE_VISIBILITY_LAST_30_DAYS
} from "../lib/settings/profileSettings.js";
import {
  EXPENSE_TYPE_IRREGULAR,
  EXPENSE_TYPE_ONGOING,
  getExpenseTypeTranslationKey
} from "../lib/expenseType.js";
import { parseExpenseSearchQuery } from "./expenseSearchQuery.js";
import {
  buildAppliedVisibleRowIds,
  buildExpenseTableView,
  compactRows,
  createDuplicateExpenseRow,
  createEmptyExpenseRow,
  isCompleteExpenseRow,
  isEmptyExpenseRow,
  sortExpenseRowsNewestFirst,
  syncAppliedVisibleRowIds
} from "./expensePageRows.js";
import { useExpensePageDataLoader } from "./useExpensePageDataLoader.js";
import {
  createExpenseVisibilityFilterBindings,
  useExpenseContextToolbar
} from "./useExpensePageInteractions.js";

const expensesPageLayout = createDashboardLayout({
  columns: [
    { size: "fill" }
  ],
  rows: [
    { size: "fill" }
  ],
  areas: [
    ["grid"]
  ]
});

const expenseVisibilityOptions = [
  { value: "this-month", labelKey: "thisMonth" },
  { value: "last-month", labelKey: "lastMonth" },
  { value: "this-and-last-month", labelKey: "thisAndLastMonth" },
  { value: "last-10", labelKey: "last10" },
  { value: "last-30", labelKey: "last30" },
  { value: "last-10-days", labelKey: "last10Days" },
  { value: "last-30-days", labelKey: "last30Days" },
  { value: "all", labelKey: "all" }
];
const EXPENSES_SORT_SESSION_KEY = "wallet-watch:expenses-sort";
const EXPENSES_SELECTION_SESSION_KEY = "wallet-watch:expenses-selection";
const EXPENSE_VISIBILITY_OPTION_VALUES = {
  all: EXPENSE_VISIBILITY_ALL,
  last10: EXPENSE_VISIBILITY_LAST_10,
  last30: EXPENSE_VISIBILITY_LAST_30,
  last10Days: EXPENSE_VISIBILITY_LAST_10_DAYS,
  last30Days: EXPENSE_VISIBILITY_LAST_30_DAYS
};

export function ExpensesPage({
  activeScope,
  onNavigateToPage,
  onExpensesChanged,
  onLoadSampleData,
  onStartBudgetEntry,
  visibilityFilter,
  onVisibilityFilterChange,
  showAltHints = false,
  t = (key) => key
}) {
  const pageRef = useRef(null);
  const searchInputRef = useRef(null);
  const visibilitySelectRef = useRef(null);
  const gridApiRef = useRef(null);
  const [gridReady, setGridReady] = useState(false);
  const didRestoreSelectionRef = useRef(false);
  const [rows, setRows] = useState([]);
  const [appliedVisibleRowIds, setAppliedVisibleRowIds] = useState(() => new Set());
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [expenseScope, setExpenseScope] = useState(null);
  const [isReady, setIsReady] = useState(false);
  const [searchQuery, setSearchQuery] = useSessionStorageState(EXPENSES_SEARCH_SESSION_KEY, "");
  const [sortState, setSortState] = useRememberedSortState(EXPENSES_SORT_SESSION_KEY);
  const [rememberedSelection, setRememberedSelection] = usePageStateMemory(EXPENSES_SELECTION_SESSION_KEY, {
    rowId: null,
    columnKey: "categoryId"
  }, {
    deserialize(storedValue) {
      try {
        const parsed = JSON.parse(storedValue);
        return {
          rowId: typeof parsed?.rowId === "string" ? parsed.rowId : null,
          columnKey: typeof parsed?.columnKey === "string" ? parsed.columnKey : "categoryId"
        };
      } catch {
        return { rowId: null, columnKey: "categoryId" };
      }
    },
    serialize(value) {
      return JSON.stringify({
        rowId: value?.rowId ?? null,
        columnKey: value?.columnKey ?? "categoryId"
      });
    }
  });
  const rowsRef = useRef([]);
  const latestSaveRequestRef = useRef(0);
  const { handleSelectionChange, selectedCellState } = useStableSelectionState();
  const [focusContext, setFocusContext] = useState(null);
  const activeProfileId = activeScope?.profileId ?? null;
  const activeYear = activeScope?.year ?? null;

  const focusMainArea = useCallback(() => {
    const gridElement = pageRef.current?.querySelector('[data-role="data-grid"]');
    gridElement?.focus({ preventScroll: true });
  }, []);

  useEffect(() => {
    rowsRef.current = compactRows(rows);
  }, [rows]);

  useExpensePageDataLoader({
    activeProfileId,
    activeYear,
    setCategoryOptions,
    setExpenseScope,
    setIsReady,
    setRows
  });

  const localizedVisibilityOptions = useMemo(
    () => expenseVisibilityOptions.map((option) => ({
      ...option,
      label: t(option.labelKey)
    })),
    [t]
  );
  const visibilityFilterBindings = useMemo(() => createExpenseVisibilityFilterBindings(
    localizedVisibilityOptions,
    visibilityFilter,
    onVisibilityFilterChange
  ), [localizedVisibilityOptions, onVisibilityFilterChange, visibilityFilter]);
  const canEnterExpenses = categoryOptions.length > 0;
  const rowEditing = useMemo(() => ({
    appendEmptyRow: canEnterExpenses,
    allowDeleteRow: true,
    createEmptyRow: () => createEmptyExpenseRow(activeYear),
    createDuplicateRow: (row) => createDuplicateExpenseRow(row, activeYear),
    isRowEmpty: isEmptyExpenseRow,
    isRowComplete: isCompleteExpenseRow
  }), [activeYear, canEnterExpenses]);
  const paymentMethodOptions = useMemo(() => listExpenseMethodOptions(rows), [rows]);
  const supplierOptions = useMemo(() => listExpenseSupplierOptions(rows), [rows]);
  const serviceOptions = useMemo(() => listExpenseServiceOptions(rows), [rows]);
  const searchQueryGroups = useMemo(
    () => parseExpenseSearchQuery(searchQuery),
    [searchQuery]
  );
  const expenseTypeLabels = useMemo(() => ({
    [EXPENSE_TYPE_ONGOING]: t(getExpenseTypeTranslationKey(EXPENSE_TYPE_ONGOING)),
    [EXPENSE_TYPE_IRREGULAR]: t(getExpenseTypeTranslationKey(EXPENSE_TYPE_IRREGULAR))
  }), [t]);
  const expenseSearchColumns = useMemo(() => createExpenseGridColumns({
    activeYear,
    categoryOptions,
    t
  }), [activeYear, categoryOptions, t]);
  const {
    expenseGridColumns,
    expenseHeaderGroups,
    hiddenMatchingCount,
    visibleRows,
    visibleTotal
  } = useMemo(() => buildExpenseTableView({
    activeYear,
    appliedVisibleRowIds,
    categoryOptions,
    rows,
    searchQueryGroups,
    sortState,
    supplierOptions,
    serviceOptions,
    paymentMethodOptions,
    t,
    visibilityFilter,
    expenseTypeLabels,
    visibilityOptions: EXPENSE_VISIBILITY_OPTION_VALUES
  }), [
    activeYear,
    appliedVisibleRowIds,
    categoryOptions,
    rows,
    searchQueryGroups,
    sortState,
    supplierOptions,
    serviceOptions,
    paymentMethodOptions,
    t,
    visibilityFilter,
    expenseTypeLabels
  ]);

  const applySearchFilterRequest = useCallback((nextSearchQuery = "") => {
    const nextSearchQueryGroups = parseExpenseSearchQuery(nextSearchQuery);

    setSearchQuery(nextSearchQuery);
    setAppliedVisibleRowIds(buildAppliedVisibleRowIds(
      rowsRef.current,
      visibilityFilter,
      nextSearchQueryGroups,
      expenseTypeLabels,
      expenseSearchColumns,
      isCompleteExpenseRow,
      EXPENSE_VISIBILITY_OPTION_VALUES
    ));
  }, [expenseSearchColumns, expenseTypeLabels, setSearchQuery, visibilityFilter]);
  const reapplyVisibleFilter = useCallback(() => {
    applySearchFilterRequest(searchQuery);
  }, [applySearchFilterRequest, searchQuery]);
  const hiddenScopeAvailable = hiddenMatchingCount > 0 && visibilityFilter !== EXPENSE_VISIBILITY_ALL;
  const showAllMatchingRows = useCallback(() => {
    onVisibilityFilterChange?.(EXPENSE_VISIBILITY_ALL);
    focusMainArea();
  }, [focusMainArea, onVisibilityFilterChange]);
  const showAllMatchingRowsBindings = useMemo(() => (
    hiddenScopeAvailable
      ? [onKey("Alt+A", showAllMatchingRows, { allowInInput: true })]
      : []
  ), [hiddenScopeAvailable, showAllMatchingRows]);

  useEffect(() => {
    if (!isReady) {
      return;
    }

    reapplyVisibleFilter();
  }, [isReady, reapplyVisibleFilter]);
  const {
    canOpenContextToolbar,
    pageBindings,
    renderedToolbarActions,
    toolbarActions,
    toolbarOpen,
    setSelectedIndex,
    setToolbarOpen
  } = useExpenseContextToolbar({
    focusContext,
    focusMainArea,
    gridApiRef,
    onFilterRequest: applySearchFilterRequest,
    onNavigateToPage,
    searchInputRef,
    searchQuery,
    selectedCellState,
    setSearchQuery,
    setSortState,
    sortState,
    t,
    visibilitySelectRef
  });

  async function handleRowsChange(updateRows) {
    const nextRows = sortExpenseRowsNewestFirst(compactRows(updateRows(compactRows(rowsRef.current))));
    const persistedRows = nextRows.filter(isCompleteExpenseRow);
    rowsRef.current = nextRows;
    setRows(nextRows);

    if (!expenseScope) {
      return;
    }

    const requestId = latestSaveRequestRef.current + 1;
    latestSaveRequestRef.current = requestId;

    try {
      await updateExpenseRows(expenseScope.profileId, expenseScope.year, persistedRows, {
        allowEmptyReplace: persistedRows.length === 0
      });
      await onExpensesChanged?.(expenseScope);
    } catch (error) {
      console.error("Failed to save expense rows.", error);
      if (latestSaveRequestRef.current !== requestId) {
        return;
      }

      try {
        const [refreshedRows, refreshedCategoryOptions] = await Promise.all([
          listExpenseRows(expenseScope.profileId, expenseScope.year),
          listBudgetCategoryOptions(expenseScope.profileId, expenseScope.year)
        ]);
        rowsRef.current = compactRows(refreshedRows);
        setCategoryOptions(refreshedCategoryOptions);
        setRows(compactRows(refreshedRows));
      } catch (refreshError) {
        console.error("Failed to recover expense rows after save failure.", refreshError);
      }
    }
  }

  async function handleFilteredRowsChange(updateRows) {
    const currentVisibleRows = compactRows(visibleRows);
    const nextVisibleRows = sortExpenseRowsNewestFirst(compactRows(updateRows(currentVisibleRows)));
    const visibleIds = new Set(currentVisibleRows.map((row) => row.id).filter(Boolean));
    const nextVisibleRowsById = new Map(nextVisibleRows.map((row) => [row.id, row]));
    const nextRows = [];

    setAppliedVisibleRowIds((currentValue) => syncAppliedVisibleRowIds(
      currentValue,
      currentVisibleRows,
      nextVisibleRows,
      isCompleteExpenseRow
    ));

    compactRows(rowsRef.current).forEach((row) => {
      if (!visibleIds.has(row.id)) {
        nextRows.push(row);
        return;
      }

      const nextVisibleRow = nextVisibleRowsById.get(row.id);
      if (nextVisibleRow) {
        nextRows.push(nextVisibleRow);
      }
    });

    nextVisibleRows.forEach((row) => {
      if (!visibleIds.has(row.id)) {
        nextRows.push(row);
      }
    });

    await handleRowsChange(() => sortExpenseRowsNewestFirst(nextRows));
  }

  useShortcutBindings(window, visibilityFilterBindings);
  useShortcutBindings(window, pageBindings);
  useShortcutBindings(window, showAllMatchingRowsBindings);

  useEffect(() => {
    if (!didRestoreSelectionRef.current) {
      return;
    }

    if (!selectedCellState?.row?.id || !selectedCellState?.column?.key) {
      return;
    }

    setRememberedSelection({
      rowId: selectedCellState.row.id,
      columnKey: selectedCellState.column.key
    });
  }, [selectedCellState, setRememberedSelection]);

  useEffect(() => {
    if (!gridReady || !gridApiRef.current || !isReady) {
      return;
    }

    if (didRestoreSelectionRef.current) {
      return;
    }

    didRestoreSelectionRef.current = true;
    gridApiRef.current.restoreSelection({
      rowId: rememberedSelection?.rowId ?? null,
      columnKey: rememberedSelection?.columnKey ?? "categoryId",
      focus: true
    });
  }, [gridReady, isReady, rememberedSelection?.columnKey, rememberedSelection?.rowId]);

  return (
    <DashboardPage
      layout={expensesPageLayout}
      slots={[
        {
          key: "grid",
          area: "grid",
          content: isReady ? (
            <div class="fill-area" ref={pageRef}>
              <Panel
                className="fill-area"
                toolbar={(
                  <ControlBar
                    boxed={false}
                    start={(
                      <AltHintTarget label="Q" visible={showAltHints}>
                        <SearchField
                          className="expenses-search-input"
                          inputRef={searchInputRef}
                          onFocusMainArea={focusMainArea}
                          onSubmit={reapplyVisibleFilter}
                          onValueChange={setSearchQuery}
                          placeholder={t("filter")}
                          value={searchQuery}
                        />
                      </AltHintTarget>
                    )}
                    middle={(
                      <div class="control-bar-summary">
                        {formatCurrency(visibleTotal)}
                      </div>
                    )}
                    end={(
                      <>
                        {hiddenMatchingCount > 0 ? (
                          <AltHintTarget
                            as="span"
                            className="expenses-hidden-warning-hint"
                            label="A"
                            visible={showAltHints}
                          >
                            <button
                              class="expenses-hidden-warning"
                              type="button"
                              onClick={showAllMatchingRows}
                            >
                              <StatusMessage type="warning" message={`+${hiddenMatchingCount} ${t("hidden")}`} />
                            </button>
                          </AltHintTarget>
                        ) : null}
                        <AltHintTarget label="E" visible={showAltHints}>
                          <select
                            class="control-bar-select expenses-visibility-select"
                            ref={visibilitySelectRef}
                            value={visibilityFilter}
                            onInput={(event) => onVisibilityFilterChange(event.currentTarget.value)}
                          >
                            {localizedVisibilityOptions.map((option) => (
                              <option key={option.value} value={option.value}>
                                {option.label}
                              </option>
                            ))}
                          </select>
                        </AltHintTarget>
                        <AltHintTarget label="N" visible={showAltHints}>
                          <button
                            class="control-bar-button"
                            type="button"
                            disabled={!canEnterExpenses}
                            onClick={() => gridApiRef.current?.startDraftRowEdit()}
                          >
                            {t("new")}
                          </button>
                        </AltHintTarget>
                      </>
                    )}
                  />
                )}
                footer={!canEnterExpenses ? t("messages.sampleData.expensesEmptyBody") : null}
                footerEnd={!canEnterExpenses ? (
                  <>
                    <button
                      class="settings-action-button primary"
                      type="button"
                      onClick={() => onLoadSampleData?.()}
                    >
                      {t("messages.sampleData.loadSampleData")}
                    </button>
                    <button
                      class="settings-action-button"
                      type="button"
                      onClick={() => onStartBudgetEntry?.()}
                    >
                      {t("messages.sampleData.addBudgetCategory")}
                    </button>
                  </>
                ) : null}
              >
                <AltHintTarget label="W" visible={showAltHints} fill>
                  <>
                    <DataGrid
                      columns={expenseGridColumns}
                      headerGroups={expenseHeaderGroups}
                      showGroupedLeafHeaders={false}
                      rows={visibleRows}
                      onRowsChange={handleFilteredRowsChange}
                      rowEditing={rowEditing}
                      sortState={sortState}
                      onSortChange={setSortState}
                      onApiReady={(api) => {
                        gridApiRef.current = api;
                        setGridReady(Boolean(api));
                      }}
                      onFocusContextChange={setFocusContext}
                      onSelectionChange={handleSelectionChange}
                      embedded
                    />
                    <FloatingActionToolbar
                      actions={renderedToolbarActions}
                      anchorElement={canOpenContextToolbar ? selectedCellState?.cellElement : null}
                      isOpen={toolbarOpen && canOpenContextToolbar}
                      offset={4}
                      onActionClick={(actionKey) => {
                        const actionIndex = toolbarActions.findIndex((action) => action.key === actionKey);
                        if (actionIndex < 0) {
                          return;
                        }

                        setSelectedIndex(actionIndex);
                        setToolbarOpen(false);
                        toolbarActions[actionIndex].onSelect?.();
                      }}
                    />
                  </>
                </AltHintTarget>
              </Panel>
            </div>
        ) : null
        }
      ]}
    />
  );
}
